'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams, usePathname } from 'next/navigation';
import { getApplicationsMenuHref } from '@/lib/application-flow';
import { Logo, LogoIcon, LogoPartners } from '@/components/shared/logo';
import { LanguageSwitcher } from '@/components/shared/language-switcher';
import { DoctorHeader } from '@/components/shared/doctor-header';
import { ReportIssueModal } from '@/components/application/report-issue-modal';
import { useTranslation } from '@/contexts/language-context';
import { signOut } from '@/components/application/actions';
import {
  CogOutline,
  LogOutOutline,
  MenuOutline,
  AddOutline,
  BugOutline
} from '@raresail/react-ionicons';

export function AppHeader({
  children,
  user,
  partner = false,
  linkText,
  linkHref = '/',
  showDoctorHeader = true,
  mainClassName,
  hasDeviceFinancingApplications = false,
  hasBorrowerApplications = false
}) {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const showNewApplicationButton = pathname === '/';
  const [showTicketModal, setShowTicketModal] = useState(false);

  const isFinancing = useMemo(() => {
    if (
      pathname.startsWith('/financing') ||
      pathname.startsWith('/us/financing')
    ) {
      return true;
    }
    const callbackUrl = searchParams.get('callbackUrl') || '';
    try {
      const callbackPath = callbackUrl.startsWith('/')
        ? callbackUrl
        : new URL(callbackUrl).pathname;
      return (
        callbackPath.startsWith('/financing') ||
        callbackPath.startsWith('/us/financing')
      );
    } catch {
      return false;
    }
  }, [pathname, searchParams]);

  const applicationsHref = useMemo(
    () =>
      getApplicationsMenuHref(pathname, {
        hasDeviceFinancingApplications,
        hasBorrowerApplications
      }),
    [pathname, hasDeviceFinancingApplications, hasBorrowerApplications]
  );

  const isLoggedIn = !!user;
  const isApplicationsPage = isLoggedIn && pathname === '/';
  const contentMaxWidth = isApplicationsPage
    ? 'max-w-7xl'
    : 'max-w-form-content';

  return (
    <div className="min-h-[calc(100vh-150px)] relative">
      {showDoctorHeader && <DoctorHeader />}
      <div className="w-full bg-base-100">
        <div className="navbar mx-auto max-w-7xl px-2">
          <div className="navbar-start">
            <Link href="/" className="ml-2">
              {(partner || isFinancing) ? <LogoPartners className="w-28 md:w-40" /> : <Logo className="w-28 md:w-40" />}
            </Link>
          </div>
          <div className="navbar-center hidden lg:flex" />
          <div className="navbar-end flex gap-2 mr-2">
            {linkText && (
              <Link
                href={linkHref}
                className="btn btn-sm btn-neutral rounded-full px-6 cursor-pointer"
              >
                {linkText}
              </Link>
            )}
            <LanguageSwitcher containerClassName="dropdown-end" />

            {isLoggedIn && showNewApplicationButton && (
              <>
                <Link
                  href="/applications/apply"
                  className="btn btn-sm btn-neutral rounded-full px-6 cursor-pointer ml-2 hidden md:inline-flex"
                >
                  {t(
                    'applications.actions.startNewApplication',
                    'New Application'
                  )}
                </Link>
                <Link
                  href="/applications/apply"
                  className="btn btn-sm py-0 px-2 btn-neutral btn-circle text-neutral-content ml-2 md:hidden"
                  title={t(
                    'applications.actions.startNewApplication',
                    'New Application'
                  )}
                >
                  <span className="sr-only">
                    {t(
                      'applications.actions.startNewApplication',
                      'New Application'
                    )}
                  </span>
                  <AddOutline className="w-6 h-6" />
                </Link>
              </>
            )}
            {isLoggedIn && (
              <div className="dropdown dropdown-end group ml-2">
                <div
                  tabIndex="0"
                  role="button"
                  className="btn py-0 px-3 btn-ghost bg-transparent border-0 rounded-lg cursor-pointer"
                  style={{ cursor: 'pointer' }}
                >
                  <MenuOutline className="w-6 h-6" />
                </div>
                <ul
                  tabIndex="0"
                  className="dropdown-content menu bg-base-100 rounded-box z-1 w-52 p-2 shadow"
                >
                  <li>
                    <Link
                      href={applicationsHref}
                      className="px-2"
                      onClick={() => document.activeElement.blur()}
                    >
                      <LogoIcon className="w-5 h-5" />
                      {t(
                        'applications.navbar.menu.applications',
                        'Applications'
                      )}
                    </Link>
                  </li>
                  {user?.email && (
                    <li>
                      <Link
                        href="/preferences"
                        className="px-2"
                        onClick={() => document.activeElement.blur()}
                      >
                        <CogOutline className="w-5 h-5" />
                        {t(
                          'applications.navbar.menu.preferences',
                          'Preferences'
                        )}
                      </Link>
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        document.activeElement.blur();
                        setShowTicketModal(true);
                      }}
                      className="px-2"
                    >
                      <BugOutline className="w-5 h-5" />
                      {t(
                        'applications.navbar.menu.reportIssue',
                        'Report Issue'
                      )}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        document.activeElement.blur();
                        signOut();
                      }}
                      className="px-2"
                    >
                      <LogOutOutline className="w-5 h-5" />
                      {t('applications.navbar.menu.signOut', 'Sign Out')}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

      </div>

      <div className="w-full border-b border-slate-blue-300" />

      <div className={`${contentMaxWidth} mx-auto w-full`}>
        <main className={mainClassName ?? 'w-full px-3 pt-2 md:pt-4'}>
          {children}
        </main>
      </div>

      {showTicketModal && (
        <ReportIssueModal onClose={() => setShowTicketModal(false)} />
      )}
    </div>
  );
}
